import type { Tracker } from '@/entities/tracker';
import { useAuth, useHasWorkspacePermission } from '@/entities/user';
import { isOwnTracker } from '../lib/filterTrackers';

export function canEditTracker(tracker: Pick<Tracker, 'accessLevel'>): boolean {
  return tracker.accessLevel !== 'view';
}

/**
 * `canEdit` is the caller's level on the board: it covers rows and comments,
 * and on a default board its layout and grouping too. On any other board the
 * server takes layout and grouping only from its creator and admins, yet
 * `canEdit` still reads true for every editor there, so it does not promise
 * those writes. Who manages automations comes from the automations list, not
 * from here. `canManage` covers the tracker itself (renaming, details,
 * sharing, deleting), which the server keeps to its creator and admins
 * whatever level a member holds, and on a default board to admins alone.
 * Admin is read for the selected workspace, as the server decides it, not
 * from the token.
 */
export function useTrackerAccess(
  tracker: Pick<Tracker, 'accessLevel' | 'createdBy' | 'defaultKey'>,
): {
  canEdit: boolean;
  canManage: boolean;
} {
  const currentUserId = useAuth((s) => s.signedInUser.value)?.id ?? null;
  const isWorkspaceAdmin = useHasWorkspacePermission();
  return {
    canEdit: canEditTracker(tracker),
    canManage: isWorkspaceAdmin || isOwnTracker(tracker, currentUserId),
  };
}
